import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {DefaultPaginationInfo} from '../../../../../../../core/constants/enums';
import {BizPointLoyaltyRulesDataGridColumnNames} from '../../../../../../../core/constants/table/data-grid-column-keys';
import PartnerDataGrid from '../../../../../../containers/data-grid';
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from '../../../../../../../packages/data-grid';
import useSearch from '../../../../../../hooks/use-search';
import useIsMounted from '../../../../../../hooks/use-is-mounted';
import {useConfigureNotebook} from '../../../../../../hooks/use-notebook-config';
import useBehavioralAnalyticsTool from '../../../../../../hooks/use-behavioural-analytics-tool';
import useAccessLevels from '../../../../../../hooks/use-access-levels';
import {AddOutlined} from '@mui/icons-material';
import Button from '../../../../../../components/base/button/button';
import SearchInput from '../../../../../../components/base/input/search_input';
import PageHeader from '../../components/page-header';
import StatusPill from '../../components/status-pill';
import {paginateMockItems} from '../../mock-search';
import {mockRules} from '../../mock-data';
import RuleBuilderDialog from './rule-builder-dialog';
import ConflictResolveDialog from './conflict-resolve-dialog';
import HistoryDialog from './history-dialog';

const dataGridSaveConfig = {name: 'BizPointLoyaltyRulesDataGrid', version: 1};
const col = BizPointLoyaltyRulesDataGridColumnNames;

const searchOptions = {
    keywordFields: ['name', 'condSummary', 'formula'],
    sortFieldMap: {
        [col.name]: 'name',
        [col.dailyCap]: 'dailyCap',
        [col.monthlyCap]: 'monthlyCap',
    },
};

const LoyaltyRulesView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.loyalty.rules'});
    const {t: tSearch} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.common.search'});
    const isMounted = useIsMounted();
    const {packages} = useAccessLevels();
    const canExportLoyaltyEarningRules = packages.bizPoint.exportLoyaltyEarningRules;
    const {configureNotebookTags} = useConfigureNotebook('biz-point-loyalty-rules');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const [sourceItems, setSourceItems] = useState(mockRules);
    const [data, setData] = useState([]);
    const [builderOpen, setBuilderOpen] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [historyRule, setHistoryRule] = useState(null);
    const [conflictRule, setConflictRule] = useState(null);

    /**
     * Configures notebook tags and behavioral analytics for the rules page.
     */
    useEffect(() => {
        configureNotebookTags((tags) => tags.bizPointLoyaltyRules);
        configureBehavioralTool((tags) => tags.bizPointLoyaltyRules);
    }, [configureBehavioralTool, configureNotebookTags]);

    const columns = useMemo(() => [
        {title: t('name'), name: col.name, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {title: t('conditions'), name: col.conditions, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left, sortable: false},
        {title: t('formula'), name: col.formula, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left, sortable: false},
        {title: t('dailyCap'), name: col.dailyCap, type: DataGridColumnTypes.number, alignment: DataGridColumnAlignments.right},
        {title: t('monthlyCap'), name: col.monthlyCap, type: DataGridColumnTypes.number, alignment: DataGridColumnAlignments.right},
        {title: t('versionColumn'), name: col.version, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left, sortable: false},
        {title: t('status'), name: col.status, type: DataGridColumnTypes.element, alignment: DataGridColumnAlignments.left, sortable: false},
        {
            title: t('actions'),
            name: col.actions,
            type: DataGridColumnTypes.element,
            alignment: DataGridColumnAlignments.left,
            sortable: false,
            pinned: true,
            pinnedToggleable: false,
            visibilityToggleable: false,
            width: 220,
        },
    ], [t]);

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(
        () => (dataGridPageSize ? {...DefaultPaginationInfo, pageSize: dataGridPageSize} : DefaultPaginationInfo),
        [dataGridPageSize],
    );

    /**
     * Loads a page of earning rules from mock data.
     */
    const search = useCallback(async (args) => {
        await Promise.resolve();
        // TODO: Replace with real API when endpoint is available
        const page = paginateMockItems(sourceItems, args, searchOptions);
        if (!isMounted()) {
            return;
        }
        setData(page);
    }, [isMounted, sourceItems]);

    const searchData = useSearch(search, {
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {keyword: ''},
    }, true);

    /**
     * Applies keyword filter via useSearch (panel SearchInput pattern).
     */
    const searchByKeyword = useCallback((keyword) => {
        searchData.setFilters({
            keyword: keyword?.toString()?.trim() ?? '',
        });
    }, [searchData]);

    const getExportInfo = useCallback(async () => {
        const items = await Promise.resolve(paginateMockItems(sourceItems, {
            filters: searchData.filters,
            orderBy: searchData.orderBy,
            paginationInfo: {currentPage: 1, pageSize: 100000},
            setPaginationInfo: () => {},
        }, searchOptions));
        if (!items?.length) {
            return undefined;
        }
        const excludeNames = ['actions'];
        const valueResolvers = {
            [col.conditions]: (item) => item.condSummary,
            [col.formula]: (item) => `${item.formula} (${item.formulaAmount})`,
            [col.version]: (item) => t('version', {ver: item.ver}),
            [col.status]: (item) => (item.active ? 'Active' : 'Inactive'),
        };
        const exportColumns = columns.filter((column) => {
            if (!column?.name || excludeNames.includes(column.name)) {
                return false;
            }
            if (column.type === DataGridColumnTypes.element && !valueResolvers[column.name]) {
                return false;
            }
            return true;
        });
        const body = items.map((item) => {
            const row = {};
            for (const column of exportColumns) {
                const header = typeof column.title === 'function'
                    ? column.title()?.toString()
                    : (column.title ?? column.name);
                if (valueResolvers[column.name]) {
                    row[header] = valueResolvers[column.name](item) ?? '--';
                } else {
                    const value = item[column.name];
                    row[header] = value == null || value === '' ? '--' : String(value);
                }
            }
            return row;
        });
        return {title: t('exportTitle'), body};
    }, [columns, searchData.filters, searchData.orderBy, sourceItems, t]);

    /**
     * Opens the rule builder for a new rule.
     */
    const handleNewRule = useCallback(() => {
        setEditingRule(null);
        setBuilderOpen(true);
    }, []);

    /**
     * Opens the rule builder to edit an existing rule.
     */
    const handleEditRule = useCallback((rule) => {
        setEditingRule(rule);
        setBuilderOpen(true);
    }, []);

    /**
     * Toggles rule active state via mock update.
     */
    const handleActivate = useCallback((rule) => {
        setConflictRule(rule);
    }, []);

    /**
     * Applies rule save from builder dialog.
     */
    const handleSaveRule = useCallback((ruleData, activate) => {
        if (activate) {
            setConflictRule(ruleData);
            setBuilderOpen(false);
            return;
        }
        setSourceItems((prev) => {
            const exists = prev.find((r) => r.id === ruleData.id);
            if (exists) {
                return prev.map((r) => (r.id === ruleData.id ? ruleData : r));
            }
            return [...prev, ruleData];
        });
        setBuilderOpen(false);
        searchData.performSearch();
    }, [searchData]);

    /**
     * Confirms conflict resolution and activates rule.
     */
    const handleConflictResolved = useCallback((ruleData) => {
        setSourceItems((prev) => {
            const exists = prev.find((r) => r.id === ruleData.id);
            const activated = {...ruleData, active: true};
            if (exists) {
                return prev.map((r) => (r.id === ruleData.id ? activated : r));
            }
            return [...prev, activated];
        });
        setConflictRule(null);
        searchData.performSearch();
    }, [searchData]);

    const rows = useMemo(() => data.map((row) => ({
        key: row.id,
        data: row,
        cells: {
            [col.name]: row.name,
            [col.conditions]: row.condSummary,
            [col.formula]: `${row.formula} (${row.formulaAmount})`,
            [col.dailyCap]: row.dailyCap,
            [col.monthlyCap]: row.monthlyCap,
            [col.version]: t('version', {ver: row.ver}),
            [col.status]: <StatusPill status={row.active ? 'Active' : 'Inactive'}/>,
            [col.actions]:
                <Stack direction="row" spacing={1}>
                    {packages.bizPoint.loyaltyEarningRulesManage && (
                        <>
                            <Button size="small" variant="outlined" onClick={() => handleEditRule(row)}>
                                {t('edit')}
                            </Button>
                            {!row.active && (
                                <Button size="small" variant="outlined" onClick={() => handleActivate(row)}>
                                    {t('activate')}
                                </Button>
                            )}
                        </>
                    )}
                    <Button size="small" variant="outlined" onClick={() => setHistoryRule(row)}>
                        {t('historyAction')}
                    </Button>
                </Stack>,
        },
    })), [packages.bizPoint.loyaltyEarningRulesManage, data, handleActivate, handleEditRule, t]);

    return (
        <Box>
            <PageHeader
                title={t('title')}
                actions={packages.bizPoint.loyaltyEarningRulesManage
                    ? (
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<AddOutlined/>}
                            onClick={handleNewRule}
                        >
                            {t('newRule')}
                        </Button>
                    )
                    : null}
            />

            <Box sx={{mb: 3}}>
                <SearchInput
                    loading={searchData.loading}
                    autoSearch
                    immediateSearch
                    search={searchByKeyword}
                    initialValue={searchData.filters?.keyword ?? undefined}
                    label={tSearch('keyword')}
                />
            </Box>

            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{columns}}
                columns={dataGridColumns}
                rows={rows}
                state={{
                    pagination: searchData.paginationInfo,
                    sortBy: searchData.orderBy,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                hideFooterActions
                onSortByChanged={(_, current) => searchData.setOrderBy?.(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))}
                getExportInfo={canExportLoyaltyEarningRules ? getExportInfo : undefined}
            />

            <RuleBuilderDialog
                open={builderOpen}
                rule={editingRule}
                onClose={() => setBuilderOpen(false)}
                onSave={handleSaveRule}
            />

            <ConflictResolveDialog
                open={Boolean(conflictRule)}
                rule={conflictRule}
                onClose={() => setConflictRule(null)}
                onResolved={handleConflictResolved}
            />

            <HistoryDialog
                open={Boolean(historyRule)}
                rule={historyRule}
                onClose={() => setHistoryRule(null)}
            />
        </Box>
    );
};

export default LoyaltyRulesView;
